import { useEffect, useState } from "react";
import { followsApi } from "@/lib/apiClient";
import { UserPlus, UserCheck } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";

export default function FollowButton({ handle, size = "sm", initial = null, className = "", variant = "default", onStateChange }) {
    const { t } = useTranslation();
    const { user } = useAuth() || {};
    const [state, setState] = useState(initial === true ? "following" : initial === false ? "none" : initial);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        setState(initial === true ? "following" : initial === false ? "none" : initial);
    }, [handle, initial]);

    useEffect(() => {
        if (!user || !handle || state !== null) return;
        followsApi.status(handle).then((r) => setState(r.state || (r.following ? "following" : "none"))).catch(() => setState("none"));
    }, [user, handle, state]);

    if (!user || !handle || user.handle === handle) return null;

    const toggle = async (e) => {
        e.stopPropagation();
        if (busy) return;
        if (state === "incoming_request") {
            toast.message?.(`@${handle} has requested to follow you.`) || toast.success(`@${handle} has requested to follow you.`);
            return;
        }
        setBusy(true);
        try {
            if (state === "following" || state === "requested") {
                const response = await followsApi.unfollow(handle);
                const nextState = response.state || "none";
                setState(nextState);
                onStateChange?.(nextState);
                toast.success(t("followButton.toasts.unfollowed", { handle }));
            } else {
                const response = await followsApi.follow(handle);
                const nextState = response.state || (response.following ? "following" : response.requested ? "requested" : "none");
                setState(nextState);
                onStateChange?.(nextState);
                toast.success(response.requested ? `Follow request sent to @${handle}.` : t("followButton.toasts.following", { handle }));
            }
        } catch (err) {
            toast.error(t("followButton.toasts.error"));
        } finally { setBusy(false); }
    };

    if (variant === "overlay") {
        if (["following", "requested", "incoming_request"].includes(state)) return null;
        return (
            <button
                onClick={toggle}
                disabled={busy}
                className={`absolute -bottom-2 left-1/2 -translate-x-1/2 w-6 h-6 rounded-full flex items-center justify-center transition-transform hover:scale-110 ${className}`}
                style={{ background: "var(--gold)", color: "var(--navy)", zIndex: 10 }}
                data-testid={`follow-overlay-${handle}`}
                title={t("followButton.follow")}
            >
                <div className="font-bold text-lg leading-none" style={{ marginTop: "-2px" }}>+</div>
            </button>
        );
    }

    const small = size === "sm";
    return (
        <button
            onClick={toggle}
            disabled={busy}
            className={`inline-flex items-center gap-1.5 rounded-full transition-colors duration-300 ${className}`}
            style={{
                padding: small ? "5px 12px" : "9px 18px",
                fontSize: small ? 11 : 13,
                background: state === "none" ? "var(--gold)" : "transparent",
                color: state === "none" ? "var(--navy)" : state === "requested" ? "var(--gold)" : "white",
                border: state === "none" ? "1px solid var(--gold)" : `1px solid ${state === "requested" ? "rgba(212,175,55,0.4)" : "rgba(255,255,255,0.2)"}`,
                fontWeight: 500,
            }}
            data-testid={`follow-${handle}`}
        >
            {state === "following" ? <UserCheck size={12} weight="bold" /> : <UserPlus size={12} weight="bold" />}
            {state === "following"
                ? t("followButton.following")
                : state === "requested"
                    ? "Requested"
                    : state === "incoming_request"
                        ? "Requested you"
                        : t("followButton.follow")}
        </button>
    );
}
